/**
 * Unified Media Upload Modal
 * Standardized modal for uploading media with smart single/bulk detection
 */

import { useState, useEffect, useCallback } from "react";
import { Modal, Upload, Form, Input, List, Tag, message, Button } from "antd";
import { InboxOutlined, DeleteOutlined, InfoCircleOutlined, CheckCircleOutlined, AppstoreAddOutlined, PlusOutlined } from "@ant-design/icons";
import {
  validateFileSize,
  validateVideoDuration,
  getImageDimensions,
  getVideoMetadata,
  uploadToS3Storage,
  getFileNameWithoutExtension,
  capitalizeFirstLetter,
  MEDIA_CONSTANTS,
} from "../utils/mediaUtils";
import { validateAndResizeCreative } from "../../../shared/upload/validateAndResizeCreative";
import { modal as modalTokens } from "../../Tasks/configs/designTokens";

const { Dragger } = Upload;

const MediaUploadModal = ({
  visible,
  onCancel,
  onUpload,
  onAssignToGroup,
  onCreateGroup,
  mediaType = "IMAGE",
  title = "Upload Media",
  validDimensions = [],
  loading = false,
  existingCreatives = [],
  uploadedMedia = null,
}) => {
  const [uploadState, setUploadState] = useState({
    mode: "idle", // 'idle', 'single', 'bulk'
    files: [],
    validationResults: [],
    errors: [],
    currentFile: null,
    previewUrl: null,
    previewDimensions: null,
    key: null,
  });

  const [form] = Form.useForm();
  const [showRequirements, setShowRequirements] = useState(false);
  const [isUploadingPreview, setIsUploadingPreview] = useState(false);

  const clearState = useCallback(() => {
    setUploadState({
      mode: "idle",
      files: [],
      validationResults: [],
      errors: [],
      currentFile: null,
      previewUrl: null,
      previewDimensions: null,
      key: null,
    });
    form.resetFields();
  }, [form]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      clearState();
    }
  }, [clearState, visible]);

  const validateFile = async file => {
    const errors = [];

    // Check file size
    if (!validateFileSize(file, MEDIA_CONSTANTS.MAX_FILE_SIZE_MB)) {
      errors.push(
        `File size must be less than ${MEDIA_CONSTANTS.MAX_FILE_SIZE_MB}MB`
      );
    }

    // Check file type
    const allowedTypes =
      mediaType === "VIDEO"
        ? MEDIA_CONSTANTS.ALLOWED_VIDEO_TYPES
        : MEDIA_CONSTANTS.ALLOWED_IMAGE_TYPES;

    if (!allowedTypes.includes(file.type)) {
      const fileExtension = file.name.split('.').pop().toUpperCase();
      const allowedExtensions = mediaType === "VIDEO" ? ["MP4"] : ["PNG", "JPG", "JPEG", "GIF"];
      errors.push(
        `"${file.name}" is not a supported file type. ` +
        `${fileExtension} files are not allowed. ` +
        `Please upload ${mediaType === "VIDEO" ? "video" : "image"} files only: ${allowedExtensions.join(", ")}`
      );
    }

    try {
      let dimensions = { width: 0, height: 0 };
      let duration = 0;

      if (mediaType === "VIDEO") {
        const metadata = await getVideoMetadata(file);

        duration = metadata.duration;
        dimensions = { width: metadata.width, height: metadata.height };

        if (!validateVideoDuration(duration, MEDIA_CONSTANTS.VIDEO_DURATION_MIN, MEDIA_CONSTANTS.VIDEO_DURATION_MAX)) {
          errors.push(
            `Video duration must be between ${MEDIA_CONSTANTS.VIDEO_DURATION_MIN}-${MEDIA_CONSTANTS.VIDEO_DURATION_MAX} seconds`
          );
        }

        if (metadata.hasIssues && metadata.issues && metadata.issues.length > 0) {
          metadata.issues.forEach(issue => {
            if (issue.startsWith('❌')) {
              errors.push(issue.replace('❌ ', ''));
            }
          });
        }
      } else {
        // Validate and optionally auto-resize to IAB dimensions
        if (validDimensions.length > 0) {
          try {
            const result = await validateAndResizeCreative(file, validDimensions);
            // Use the (possibly resized) file for downstream upload
            file = result.file;
            dimensions = { width: result.width, height: result.height };
          } catch (err) {
            errors.push(err.message);
          }
        } else {
          dimensions = await getImageDimensions(file);
        }
      }

      return {
        valid: errors.length === 0,
        errors,
        duration,
        dimensions,
        file,
      };
    } catch (error) {
      errors.push("Failed to analyze file");
      return {
        valid: false,
        errors,
        duration: 0,
        dimensions: { width: 0, height: 0 },
        file,
      };
    }
  };

  const handleFileSelection = async info => {
    const files = info.fileList.map(
      fileInfo => fileInfo.originFileObj || fileInfo
    );

    if (files.length === 0) {
      setUploadState(prev => ({ ...prev, mode: "idle", files: [] }));
      return;
    }

    const validationResults = await Promise.all(
      files.map(file => validateFile(file))
    );

    const mode = files.length === 1 ? "single" : "bulk";

    setUploadState(prev => ({
      ...prev,
      mode,
      files,
      validationResults,
      errors: [],
      currentFile: files.length === 1 ? files[0] : null,
      previewUrl: null,
      previewDimensions: null,
      key: null,
    }));

    if (mode === "single" && validationResults[0]?.valid) {
      await setupPreview(files[0], validationResults[0]);
    }
  };

  const setupPreview = async (file, validationResult) => {
    setIsUploadingPreview(true);
    try {
      const contentType = mediaType === "VIDEO" ? "video/mp4" : "image/jpeg";
      const response = await uploadToS3Storage(file, contentType);

      setUploadState(prev => ({
        ...prev,
        previewUrl: response.url,
        previewDimensions: validationResult.dimensions,
        key: response.key,
      }));

      if (!form.getFieldValue("name")) {
        const fileName = getFileNameWithoutExtension(file.name);
        form.setFieldsValue({ name: fileName });
      }
    } catch (error) {
      message.error("Failed to upload file for preview");
    } finally {
      setIsUploadingPreview(false);
    }
  };

  const removeFile = index => {
    const newFiles = uploadState.files.filter((_, i) => i !== index);
    const newValidationResults = uploadState.validationResults.filter(
      (_, i) => i !== index
    );

    const newMode =
      newFiles.length === 0
        ? "idle"
        : newFiles.length === 1
        ? "single"
        : "bulk";

    setUploadState(prev => ({
      ...prev,
      mode: newMode,
      files: newFiles,
      validationResults: newValidationResults,
    }));

    if (newMode === "single" && newValidationResults[0]?.valid) {
      setupPreview(newFiles[0], newValidationResults[0]);
    }
  };

  const validateSingleUploadName = (_, value) => {
    if (!value) return Promise.resolve();

    const lowerValue = value.toLowerCase().trim();

    const isDuplicate = existingCreatives.some(
      creative => creative.name?.toLowerCase().trim() === lowerValue
    );

    if (isDuplicate) {
      return Promise.reject(new Error("Creative name already exists"));
    }

    return Promise.resolve();
  };

  const handleSingleUpload = async values => {
    const {
      validationResults,
      key: uploadKey,
      previewUrl,
      previewDimensions,
    } = uploadState;

    if (!uploadKey || !previewUrl) {
      message.error("Please select a file to upload");
      return;
    }

    if (validationResults.length === 0 || !validationResults[0].valid) {
      message.error("Please upload a valid file");
      return;
    }

    const { file } = validationResults[0];
    const fileName = values.name;

    const capitalizedName = capitalizeFirstLetter(fileName);

    const mediaData = {
      mediatype: mediaType,
      key: uploadKey,
      url: previewUrl,
      width: previewDimensions.width,
      height: previewDimensions.height,
      name: capitalizedName,
      size: file.size,
      fileType: file.type.split("/")[1].toUpperCase(),
    };

    onUpload([mediaData], "single");
  };

  const findUniqueName = (baseName, usedNames) => {
    let candidate = baseName;
    let counter = 1;

    while (usedNames.has(candidate.toLowerCase())) {
      candidate = `${baseName}_${counter}`;
      counter++;
    }

    return candidate;
  };

  const handleBulkUpload = () => {
    const { validationResults } = uploadState;
    const validFiles = validationResults.filter(result => result.valid);

    if (validFiles.length === 0) {
      message.error("No valid files to upload");
      return;
    }

    const invalidNames = [];
    validFiles.forEach(({ file }) => {
      const name = getFileNameWithoutExtension(file.name);
      if (name.length > 255) {
        invalidNames.push(`"${file.name}" (${name.length} characters)`);
      }
    });

    if (invalidNames.length > 0) {
      message.error(
        `Cannot upload: The following files have names exceeding 255 characters:\n${invalidNames.join('\n')}`
      );
      return;
    }

    const existingNames = new Set(
      existingCreatives
        .map(creative => creative.name?.toLowerCase().trim())
        .filter(Boolean)
    );

    const tooLongAndDuplicate = [];
    validFiles.forEach(({ file }) => {
      const baseName = getFileNameWithoutExtension(file.name);

      if (baseName.length === 255 && existingNames.has(baseName.toLowerCase())) {
        tooLongAndDuplicate.push(`"${file.name}"`);
      }
    });

    if (tooLongAndDuplicate.length > 0) {
      message.error(
        `Cannot upload: The following files have 255-character names that already exist. ` +
        `Cannot auto-deduplicate (suffix would exceed 255 character limit):\n${tooLongAndDuplicate.join('\n')}\n\n` +
        `Please rename these files to be shorter than 255 characters.`
      );
      return;
    }

    const usedNames = new Set(existingNames);
    const mediaData = validFiles.map(({ file, dimensions }) => {
      const baseName = getFileNameWithoutExtension(file.name);
      const finalName = findUniqueName(baseName, usedNames);

      usedNames.add(finalName.toLowerCase());

      return {
        file,
        dimensions,
        name: finalName,
      };
    });

    onUpload(mediaData, "bulk");
  };

  const renderDragger = (dragProps, hintText, fileCountText) => (
    <Dragger {...dragProps} className="creative-upload-modal__dragger">
      <p className="ant-upload-drag-icon creative-upload-modal__drag-icon">
        <InboxOutlined />
      </p>
      <p className="ant-upload-text creative-upload-modal__drag-text">
        {fileCountText || `Drop ${mediaType.toLowerCase()} file(s) here or click to browse`}
      </p>
      <p className="ant-upload-hint creative-upload-modal__drag-hint">
        {hintText}
      </p>
    </Dragger>
  );

  const renderContent = () => {
    const { mode, files, validationResults } = uploadState;

    const handleBeforeUpload = (file) => {
      const allowedTypes = mediaType === "VIDEO"
        ? ["video/mp4"]
        : ["image/png", "image/jpeg", "image/jpg", "image/gif"];

      if (!allowedTypes.includes(file.type)) {
        const fileExtension = file.name.split('.').pop().toUpperCase();
        const allowedExtensions = mediaType === "VIDEO" ? ["MP4"] : ["PNG", "JPG", "JPEG", "GIF"];
        message.error(
          `"${file.name}" is not supported. ${fileExtension} files are not allowed. ` +
          `Please select ${mediaType === "VIDEO" ? "video" : "image"} files only: ${allowedExtensions.join(", ")}`,
          6
        );
      }
      return false;
    };

    const dragProps = {
      multiple: true,
      accept: mediaType === "VIDEO"
        ? ".mp4,video/mp4"
        : ".png,.jpg,.jpeg,.gif,image/png,image/jpeg,image/gif",
      showUploadList: false,
      beforeUpload: handleBeforeUpload,
      onChange: handleFileSelection,
    };

    const baseHint = mediaType === "VIDEO"
      ? "MP4 format, 6-30 seconds, max 30MB per file"
      : "PNG, JPG, JPEG, or GIF format";

    if (mode === "single") {
      return (
        <div>
          {renderDragger(
            dragProps,
            <>
              {mediaType === "VIDEO" ? "MP4 format, 6-30 seconds, max 30MB" : "PNG, JPG, JPEG, or GIF format"}
              <br />Hold Ctrl/Cmd to select multiple files for bulk upload
            </>,
            `Drop ${mediaType.toLowerCase()} file here or click to browse`
          )}

          {uploadState.previewUrl && (
            <div className="creative-upload-modal__preview">
              {mediaType === "VIDEO" ? (
                <video controls className="creative-upload-modal__video-preview">
                  <source src={uploadState.previewUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={uploadState.previewUrl}
                  alt="Preview"
                />
              )}
            </div>
          )}

          {validationResults.length > 0 && !validationResults[0].valid && (
            <div className="creative-upload-modal__errors">
              {validationResults[0].errors.map((error, index) => (
                <Tag key={index} color="red">
                  {error}
                </Tag>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (mode === "bulk") {
      return (
        <div>
          {renderDragger(
            dragProps,
            <>
              {files.length} files selected
              <br />
              {baseHint}
            </>,
            `Drop ${mediaType.toLowerCase()} files here or click to browse`
          )}

          <List
            className="creative-upload-modal__file-list"
            size="small"
            dataSource={files}
            renderItem={(file, index) => {
              const validation = validationResults[index];
              return (
                <List.Item
                  actions={[
                    <DeleteOutlined
                      key="remove"
                      onClick={() => removeFile(index)}
                      className="creative-upload-modal__delete-icon"
                    />,
                  ]}
                >
                  <List.Item.Meta
                    title={file.name}
                    description={
                      <div>
                        <div>
                          Size: {(file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                        {validation && (
                          <div>
                            {validation.valid ? (
                              <Tag color="green">Valid</Tag>
                            ) : (
                              <div>
                                {validation.errors.map((error, errorIndex) => (
                                  <Tag key={errorIndex} color="red">
                                    {error}
                                  </Tag>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </div>
      );
    }

    return renderDragger(
      dragProps,
      <>
        {baseHint}
        <br />
        Select multiple files at once using Ctrl/Cmd + Click
      </>
    );
  };

  const isSuccess = uploadedMedia && uploadedMedia.length > 0;

  const renderSuccessScreen = () => {
    const count = uploadedMedia.length;
    return (
      <div className="creative-upload-modal__success">
        <div className="creative-upload-modal__success-icon">
          <CheckCircleOutlined />
        </div>
        <div className="creative-upload-modal__success-title">
          {count} Creative{count !== 1 ? "s" : ""} Uploaded
        </div>
        <div className="creative-upload-modal__success-desc">
          Your {mediaType.toLowerCase()}{count !== 1 ? "s are" : " is"} ready to use. You can assign {count !== 1 ? "them" : "it"} to an existing ad group, create a new one, or do it later.
        </div>
        <div className="creative-upload-modal__success-actions">
          <Button size="large" onClick={onCancel}>
            Done
          </Button>
          {onAssignToGroup && (
            <Button
              size="large"
              icon={<AppstoreAddOutlined />}
              onClick={() => onAssignToGroup(uploadedMedia)}
            >
              Assign to Ad Group
            </Button>
          )}
          {onCreateGroup && (
            <Button
              size="large"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => onCreateGroup(uploadedMedia)}
            >
              Create New Ad Group
            </Button>
          )}
        </div>
      </div>
    );
  };

  const getModalTitle = () => {
    if (isSuccess) return "Upload Complete";
    return (
      <div className="creative-upload-modal__title">
        <span>{title}</span>
        <Button
          type="text"
          size="small"
          icon={<InfoCircleOutlined />}
          onClick={() => setShowRequirements(true)}
          className="creative-upload-modal__req-btn"
        >
          Requirements
        </Button>
      </div>
    );
  };

  const getFooter = () => {
    if (isSuccess) return null;

    const { mode, validationResults } = uploadState;

    if (mode === "single") {
      const isValid =
        validationResults.length > 0 && validationResults[0].valid;
      return [
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          disabled={!isValid || loading || isUploadingPreview}
          loading={loading || isUploadingPreview}
          onClick={() => form.submit()}
        >
          Upload {mediaType}
        </Button>,
      ];
    }

    if (mode === "bulk") {
      const validFiles = validationResults.filter(result => result.valid);
      const hasValidFiles = validFiles.length > 0;

      return [
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          disabled={!hasValidFiles || loading || isUploadingPreview}
          loading={loading || isUploadingPreview}
          onClick={handleBulkUpload}
        >
          Upload {validFiles.length} File{validFiles.length !== 1 ? "s" : ""}
        </Button>,
      ];
    }

    return [
      <Button key="cancel" onClick={onCancel}>
        Cancel
      </Button>,
      <Button
        key="upload"
        type="primary"
        disabled={true}
        onClick={() => {}}
      >
        Upload {mediaType}
      </Button>,
    ];
  };

  return (
    <>
      {/* Requirements Modal */}
      <Modal
        title={mediaType === "VIDEO" ? "Video Upload Requirements" : "Image Upload Requirements"}
        open={showRequirements}
        onCancel={() => setShowRequirements(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowRequirements(false)}>
            Got it
          </Button>
        ]}
        width="90%"
        style={{ maxWidth: modalTokens.md }}
        centered
        className="creative-requirements-modal"
      >
        <div className="creative-requirements-modal__content">
          {mediaType === "VIDEO" ? (
            <>
              <h4>Format & Technical Specifications</h4>
              <ul>
                <li><strong>Format:</strong> MP4 only (H.264 codec required)</li>
                <li><strong>Duration:</strong> 6-30 seconds (15-30s recommended for best performance)</li>
                <li><strong>File Size:</strong> Maximum 30MB per file</li>
              </ul>

              <h4>Video Quality</h4>
              <ul>
                <li><strong>Resolution:</strong> 1920×1080 (Full HD) or 1280×720 (HD) preferred</li>
                <li><strong>Aspect Ratio:</strong> 16:9 (widescreen) or 4:3 (standard)</li>
                <li><strong>Note:</strong> CTV (Connected TV) campaigns require 16:9 format</li>
              </ul>

              <h4>Bitrate Guidelines</h4>
              <ul>
                <li><strong>Required Range:</strong> 800-5000 kbps</li>
                <li><strong>Optimal Range:</strong> 1000-3000 kbps (recommended for best compatibility)</li>
                <li><strong>Note:</strong> Higher bitrates provide better quality but larger file sizes</li>
              </ul>

              <h4>Upload Tips</h4>
              <ul>
                <li>Videos are automatically validated during upload</li>
                <li>Warnings will appear for files outside optimal ranges</li>
                <li>You can select multiple files at once for bulk upload (Ctrl/Cmd + Click)</li>
                <li>Invalid files will be highlighted with specific error messages</li>
              </ul>
            </>
          ) : (
            <>
              <h4>Supported Formats</h4>
              <ul>
                <li><strong>PNG:</strong> Best for graphics with transparency</li>
                <li><strong>JPG/JPEG:</strong> Best for photographs and complex images</li>
                <li><strong>GIF:</strong> Supported for static images</li>
              </ul>

              <h4>Image Requirements</h4>
              <ul>
                <li><strong>File Size:</strong> Maximum {MEDIA_CONSTANTS.MAX_FILE_SIZE_MB}MB per file</li>
                {validDimensions.length > 0 && (
                  <li><strong>Dimensions:</strong> {validDimensions.map(d => `${d.width}×${d.height}`).join(", ")}</li>
                )}
                <li><strong>Quality:</strong> High-resolution images recommended</li>
              </ul>

              <h4>Upload Tips</h4>
              <ul>
                <li>Images are automatically validated during upload</li>
                <li>You can select multiple files at once for bulk upload (Ctrl/Cmd + Click)</li>
                <li>Invalid files will be highlighted with specific error messages</li>
                <li>Use PNG format for images with text or logos for best clarity</li>
              </ul>
            </>
          )}
        </div>
      </Modal>

      <Modal
        title={getModalTitle()}
        open={visible}
        onCancel={onCancel}
        footer={getFooter()}
        width="90%"
        style={{ maxWidth: uploadState.mode === "bulk" ? modalTokens.wide : modalTokens.md }}
        centered
        className="creative-upload-modal"
      >
      {isSuccess ? (
        renderSuccessScreen()
      ) : uploadState.mode === "single" ? (
        <Form form={form} onFinish={handleSingleUpload}>
          {renderContent()}

          {uploadState.previewUrl && (
            <Form.Item
              label={`${mediaType} Name (Max 255 characters)`}
              name="name"
              rules={[
                {
                  required: true,
                  message: `Please enter ${mediaType.toLowerCase()} name`,
                },
                {
                  max: 255,
                  message: "Name must not exceed 255 characters",
                },
                {
                  validator: validateSingleUploadName,
                },
              ]}
              className="creative-upload-modal__name-field"
            >
              <Input
                placeholder={`Enter ${mediaType.toLowerCase()} name`}
                maxLength={255}
                showCount
                onChange={(e) => {
                  form.setFieldsValue({ name: e.target.value });
                }}
              />
            </Form.Item>
          )}
        </Form>
      ) : (
        renderContent()
      )}
    </Modal>
    </>
  );
};

export default MediaUploadModal;
